/**
 * HeroCard Component
 *
 * The main answer to "Should I buy today?" - visible within 2 seconds of page load.
 * Shows unified recommendation with visual states, data freshness, and qualifying strategy.
 */

import './HeroCard.css';

// Visual state configuration
const STATE_CONFIG = {
  ENTRY_TODAY: {
    label: 'ENTER TODAY',
    className: 'hero-entry',
    borderColor: '#4ade80',
    badgeBg: '#166534'
  },
  RECOVERY_STARTING: {
    label: 'MONITORING',
    className: 'hero-recovery',
    borderColor: '#6ee7b7',
    badgeBg: '#065f46'
  },
  DIP_DETECTED: {
    label: 'WAIT',
    className: 'hero-dip',
    borderColor: '#fbbf24',
    badgeBg: '#92400e'
  },
  NOT_QUALIFIED: {
    label: 'NO ENTRY SIGNAL',
    className: 'hero-no-entry',
    borderColor: '#4a4a4a',
    badgeBg: '#374151'
  }
};

// Strategy display names
const STRATEGY_LABELS = {
  aggressive: 'AGGRESSIVE',
  balanced: 'BALANCED',
  conservative: 'CONSERVATIVE'
};

function getDataFreshnessLevel(ageMinutes) {
  if (ageMinutes == null || ageMinutes <= 15) return 'fresh';
  if (ageMinutes <= 60) return 'stale';
  return 'very-stale';
}

function formatDataAge(ageMinutes) {
  if (ageMinutes == null) return '';
  if (ageMinutes < 1) return 'just now';
  if (ageMinutes < 60) return `${Math.round(ageMinutes)} min old`;
  const hours = Math.floor(ageMinutes / 60);
  const mins = Math.round(ageMinutes % 60);
  return mins > 0 ? `${hours}h ${mins}m old` : `${hours}h old`;
}

function buildWhyExplanation(data) {
  const parts = [];

  // Use rule_based_reason if available from unified_recommendation
  if (data.unified_recommendation?.rule_based_reason) {
    return data.unified_recommendation.rule_based_reason;
  }

  // Build explanation from available data
  const dailySignal = data.daily_signal || {};
  const recoveryStatus = data.recovery_status || {};
  const liveStatus = data.live_status || {};

  // Dip percentage
  const dipPct = dailySignal.pct_from_20d_high || data.pct_from_20d_high;
  if (dipPct != null) {
    parts.push(`Dip ${dipPct.toFixed(1)}% from high`);
  }

  // ML confidence
  const confidence = dailySignal.confidence || data.ml_confidence;
  if (confidence != null) {
    parts.push(`ML ${Math.round(confidence * 100)}% confident`);
  }

  // Recovery indicators
  const level = recoveryStatus.level || data.unified_recommendation?.level;
  if (level === 'ENTRY_TODAY' || level === 'RECOVERY_STARTING') {
    const indicators = recoveryStatus.indicators || {};
    const activeIndicators = Object.values(indicators).filter(i => i?.active).map(i => i.label);
    if (activeIndicators.length > 0) {
      parts.push(`Recovery: ${activeIndicators.slice(0, 3).join(', ')}`);
    }
  } else if (level === 'DIP_DETECTED') {
    parts.push('Waiting for recovery signals');
  } else if (level === 'NOT_QUALIFIED') {
    const minDip = -6; // Aggressive threshold
    parts.push(`Need at least ${minDip}% dip to trigger`);
  }

  return parts.join(', ');
}

function HeroCard({
  ticker,
  data,
  dataAgeMinutes,
  marketOpen,
  onRefresh,
  loading
}) {
  // Determine the visual state
  const unifiedRec = data?.unified_recommendation || {};
  const dailySignal = data?.daily_signal || {};
  const liveStatus = data?.live_status || {};

  // Determine level from various sources
  let level = unifiedRec.level || data?.recovery_status?.level || 'NOT_QUALIFIED';

  // If daily signal says no entry, override to NOT_QUALIFIED
  if (dailySignal.entry_day === false && !data?.strategies?.some(s => s.entry_today)) {
    level = 'NOT_QUALIFIED';
  }

  // If any strategy qualifies but recovery level isn't set, use DIP_DETECTED
  const anyStrategyQualifies = data?.strategies?.some(s => s.entry_today);
  if (anyStrategyQualifies && level === 'NOT_QUALIFIED') {
    level = data?.recovery_status?.level || 'DIP_DETECTED';
  }

  const stateConfig = STATE_CONFIG[level] || STATE_CONFIG.NOT_QUALIFIED;

  // Find qualifying strategy
  const qualifyingStrategy = dailySignal.qualifying_strategy ||
    data?.strategies?.find(s => s.entry_today)?.strategy_key || null;

  // Current price
  const currentPrice = liveStatus.current_price || dailySignal.price || data?.price;

  // Data freshness
  const freshnessLevel = getDataFreshnessLevel(dataAgeMinutes);
  const showFreshnessWarning = freshnessLevel !== 'fresh';

  // Build why explanation
  const whyExplanation = buildWhyExplanation(data);

  return (
    <div className={`hero-card ${stateConfig.className}`}>
      {/* Data Freshness Warning */}
      {showFreshnessWarning && (
        <div className={`freshness-warning freshness-${freshnessLevel}`}>
          <span className="warning-icon">⚠️</span>
          <span>Data {formatDataAge(dataAgeMinutes)} - may be outdated</span>
        </div>
      )}

      {/* Header Row */}
      <div className="hero-header">
        <div className="hero-live-indicator">
          {marketOpen ? (
            <span className="live-badge live">● LIVE</span>
          ) : (
            <span className="live-badge closed">Market Closed</span>
          )}
        </div>
        <button
          className="hero-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? '...' : '↻'}
        </button>
      </div>

      {/* Main Content */}
      <div className="hero-content">
        {/* Ticker */}
        <div className="hero-ticker">{ticker}</div>

        {/* Main Recommendation */}
        <div className="hero-recommendation">
          {stateConfig.label}
        </div>

        {/* Qualifying Strategy Badge */}
        {qualifyingStrategy && level !== 'NOT_QUALIFIED' && (
          <div
            className="hero-strategy-badge"
            style={{ backgroundColor: stateConfig.badgeBg }}
          >
            {STRATEGY_LABELS[qualifyingStrategy] || qualifyingStrategy.toUpperCase()}
          </div>
        )}

        {/* Current Price */}
        <div className="hero-price">
          ${currentPrice?.toFixed(2) || '--'}
        </div>

        {/* Why Explanation */}
        <div className="hero-why">
          <span className="why-label">Why?</span>
          <span className="why-text">{whyExplanation}</span>
        </div>
      </div>
    </div>
  );
}

export default HeroCard;
